'use client';

import { alpha, Box, Button, Stack, Theme, Typography } from '@mui/material';

import { useT } from '@/hooks';
import type { EventMarket, EventOutcome } from '@/types';
import { filterAndSortMarkets, getMarketProbability, normalizeMarketQuestion } from '@/utils';
import type { CardVariantProps } from './types';

interface TradeButtonProps {
  outcomeId: string;
  subOutcomeId: string;
  name: string;
  isFirst: boolean;
  onTradeClick?: (marketId: string, outcomeId: string, isBuy: boolean) => void;
}

const getButtonStyles = (theme: Theme, isFirst: boolean) => {
  const variant = isFirst ? theme.palette.success : theme.palette.error;

  // Design (Landing.dc.html outcome row): compact Yes/No pill — 7px radius,
  // 12.5px / 600, tinted fill + subtle border. Colours stay on the theme's
  // success/error tokens (already warm) so light + dark both render correctly.
  return {
    minWidth: { xs: 54, sm: 46 },
    height: { xs: 32, sm: 26 },
    borderRadius: '7px',
    textTransform: 'none' as const,
    fontWeight: 600,
    fontSize: { xs: '13.5px', sm: '12.5px' },
    lineHeight: 1,
    boxShadow: 'none',
    bgcolor: variant.bg,
    color: theme.palette.mode === 'dark' ? variant.light : variant.dark,
    border: '1px solid',
    borderColor: variant.border,
    padding: { xs: '0 15px', sm: '0 13px' },
    '&:hover': {
      bgcolor: alpha(variant.main, 0.25),
      boxShadow: 'none',
    },
  };
};

function TradeButton({ outcomeId, subOutcomeId, name, isFirst, onTradeClick }: TradeButtonProps) {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onTradeClick?.(outcomeId, subOutcomeId, true);
  };

  const sxStyles = (theme: Theme) => getButtonStyles(theme, isFirst);

  return (
    <Button variant="contained" size="small" onClick={handleClick} sx={sxStyles}>
      <Box
        component="span"
        sx={{
          width: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'block',
          textAlign: 'center',
        }}
      >
        {name}
      </Box>
    </Button>
  );
}

interface OutcomeRowProps {
  market: EventMarket;
  onTradeClick?: (marketId: string, outcomeId: string, isBuy: boolean) => void;
}

function OutcomeRow({ market, onTradeClick }: OutcomeRowProps) {
  const probability = getMarketProbability(market);

  let outcomesArray: EventOutcome[] = [];
  try {
    outcomesArray =
      typeof market.outcomes === 'string' ? JSON.parse(market.outcomes) : market.outcomes;
  } catch {
    outcomesArray = [];
  }
  if (!Array.isArray(outcomesArray)) outcomesArray = [];

  const renderSubOutcome = (oc: EventOutcome, idx: number) => (
    <TradeButton
      key={oc.id || idx}
      outcomeId={market.id}
      subOutcomeId={oc.id}
      name={oc.name}
      isFirst={idx === 0}
      onTradeClick={onTradeClick}
    />
  );

  const subOutcomes = outcomesArray.slice(0, 2).map(renderSubOutcome);
  const name = normalizeMarketQuestion(market);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        py: 0.25,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          // Design: 13.5px regular, muted label, ellipsis.
          fontWeight: 500,
          color: 'text.secondary',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
          fontSize: '13.5px',
        }}
      >
        {name}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          variant="body2"
          sx={{
            // Design: mono 13px / 600, fixed 44px column, primary text.
            fontFamily: 'var(--font-plex-mono), IBM Plex Mono, monospace',
            fontWeight: 600,
            color: 'text.primary',
            minWidth: '44px',
            textAlign: 'right' as const,
            fontSize: '13px',
          }}
        >
          {probability}
        </Typography>

        <Box sx={{ display: 'flex', gap: 0.5 }}>{subOutcomes}</Box>
      </Box>
    </Box>
  );
}

export function MultiOutcomeCard({ event, onTradeClick }: CardVariantProps) {
  const { t } = useT();
  const markets = event.markets || [];

  const sortedMarkets = filterAndSortMarkets(markets);
  const visibleMarkets = sortedMarkets.slice(0, 2);
  const remainingCount = Math.max(sortedMarkets.length - visibleMarkets.length, 0);
  const renderOutcomeRow = (market: EventMarket) => (
    <OutcomeRow key={market.id} market={market} onTradeClick={onTradeClick} />
  );

  const rows = visibleMarkets.map(renderOutcomeRow);

  return (
    <Stack spacing={1} sx={{ overflow: 'hidden' }}>
      {rows}
      {remainingCount > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            textAlign: 'center',
            display: 'block',
            mt: 0.5,
            fontWeight: 600,
            opacity: 0.8,
            cursor: 'pointer',
          }}
        >
          + {remainingCount} {t('nav.more')?.toLowerCase()}
        </Typography>
      )}
    </Stack>
  );
}
